/**
 * Shopify Cart Auto-Expiration Handler
 * Automatically removes pre-order items with delivery dates after 5 days
 * Regular items without delivery dates are kept in the cart
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    expirationDays: 5,
    storageKey: 'shopify_cart_timestamp',
    debugMode: true, // Set to false to hide console logs
    deliveryDateProperty: '_is_preorder' // The property name Globo uses for delivery dates
  };

  // Utility function for logging
  function log(message, data) {
    if (CONFIG.debugMode) {
      console.log('[Cart Expiration]', message, data || '');
    }
  }

  // Get cart creation timestamp from localStorage
  function getCartTimestamp() {
    try {
      const timestamp = localStorage.getItem(CONFIG.storageKey);
      return timestamp ? parseInt(timestamp, 10) : null;
    } catch (e) {
      log('Error reading from localStorage:', e);
      return null;
    }
  }

  // Set cart creation timestamp in localStorage
  function setCartTimestamp(timestamp) {
    try {
      localStorage.setItem(CONFIG.storageKey, timestamp.toString());
      log('Cart timestamp set:', new Date(timestamp).toISOString());
    } catch (e) {
      log('Error writing to localStorage:', e);
    }
  }

  // Remove cart timestamp from localStorage
  function removeCartTimestamp() {
    try {
      localStorage.removeItem(CONFIG.storageKey);
      log('Cart timestamp removed');
    } catch (e) {
      log('Error removing from localStorage:', e);
    }
  }

  // Get current cart
  function getCart() {
    return fetch('/cart.js')
      .then(response => response.json())
      .catch(error => {
        log('Error fetching cart:', error);
        return null;
      });
  }

  // Remove specific items from cart by their keys
  function removeItemsFromCart(itemKeys) {
    if (!itemKeys || itemKeys.length === 0) {
      log('No items to remove');
      return Promise.resolve();
    }

    log('Removing items with keys:', itemKeys);

    // Build updates object to set quantities to 0 for expired items
    const updates = {};
    itemKeys.forEach(key => {
      updates[key] = 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    })
    .then(response => response.json())
    .then(cart => {
      log('Items removed successfully', cart);
      return cart;
    })
    .catch(error => {
      log('Error removing items:', error);
      throw error;
    });
  }

  // Check if an item has a delivery date property
  function hasDeliveryDate(item) {
    return item.properties && 
           CONFIG.deliveryDateProperty in item.properties &&
           item.properties[CONFIG.deliveryDateProperty];
  }

  // Calculate if cart has expired
  function isCartExpired(timestamp) {
    const now = Date.now();
    const expirationTime = CONFIG.expirationDays * 24 * 60 * 60 * 1000;
    const age = now - timestamp;
    
    log('Cart age check:', {
      created: new Date(timestamp).toISOString(),
      now: new Date(now).toISOString(),
      ageInDays: (age / (24 * 60 * 60 * 1000)).toFixed(2),
      isExpired: age > expirationTime
    });
    
    return age > expirationTime;
  }

  // Main function to check and handle cart expiration
  function handleCartExpiration() {
    getCart().then(cart => {
      if (!cart || cart.item_count === 0) {
        // Cart is empty, remove timestamp
        removeCartTimestamp();
        log('Cart is empty, timestamp removed');
        return;
      }

      log('Cart has items:', cart.item_count);

      // Check which items have delivery dates
      const itemsWithDeliveryDates = cart.items.filter(hasDeliveryDate);
      const itemsWithoutDeliveryDates = cart.items.filter(item => !hasDeliveryDate(item));

      log('Items with delivery dates:', itemsWithDeliveryDates.length);
      log('Items without delivery dates (will be kept):', itemsWithoutDeliveryDates.length);

      // If no items have delivery dates, remove timestamp and exit
      if (itemsWithDeliveryDates.length === 0) {
        removeCartTimestamp();
        log('No pre-order items in cart, timestamp removed');
        return;
      }

      // Check for existing timestamp
      const existingTimestamp = getCartTimestamp();
      
      if (!existingTimestamp) {
        // First time seeing this cart with pre-order items, set timestamp
        const now = Date.now();
        setCartTimestamp(now);
        log('Cart with pre-order items detected, timestamp set');
        return;
      }

      // Check if cart has expired
      if (isCartExpired(existingTimestamp)) {
        log('Cart has expired, removing pre-order items...');
        
        // Get the keys of items with delivery dates
        const keysToRemove = itemsWithDeliveryDates.map(item => item.key);
        
        log('Items to remove:', itemsWithDeliveryDates.map(item => ({
          title: item.title,
          delivery_date: item.properties[CONFIG.deliveryDateProperty]
        })));

        removeItemsFromCart(keysToRemove)
          .then(() => {
            log('Pre-order items cleared');
            
            // Remove timestamp since we've cleared the expired items
            removeCartTimestamp();
            
            // Show message to user
            const itemCount = keysToRemove.length;
            const message = itemCount === 1
              ? 'Items with an outdated delivery date have been removed from your cart.'
              : `${itemCount} items with outdated delivery dates have been removed from your cart.`;
            
            alert(message);
            
            // Reload the page to reflect the updated cart
            window.location.reload();
          })
          .catch(error => {
            log('Failed to clear pre-order items:', error);
          });
      } else {
        log('Cart is still valid');
      }
    });
  }

  // Listen for cart updates to reset timestamp when cart changes
  function setupCartUpdateListener() {
    // Listen for custom cart update events (if your theme dispatches them)
    document.addEventListener('cart:updated', () => {
      log('Cart updated event detected');
      
      // Check if there are still pre-order items, if so update timestamp
      getCart().then(cart => {
        if (cart && cart.items.some(hasDeliveryDate)) {
          const now = Date.now();
          setCartTimestamp(now);
        } else {
          removeCartTimestamp();
        }
      });
    });

    // Also listen for Shopify's native cart events if available
    if (window.Shopify && window.Shopify.onCartUpdate) {
      const originalOnCartUpdate = window.Shopify.onCartUpdate;
      window.Shopify.onCartUpdate = function(cart) {
        log('Shopify cart update detected');
        
        getCart().then(cart => {
          if (cart && cart.items.some(hasDeliveryDate)) {
            const now = Date.now();
            setCartTimestamp(now);
          } else {
            removeCartTimestamp();
          }
        });
        
        if (originalOnCartUpdate) {
          originalOnCartUpdate(cart);
        }
      };
    }
  }

  // Initialize the cart expiration handler
  function init() {
    log('Cart Expiration Handler initialized (Selective Mode)');
    
    // Check cart expiration on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleCartExpiration);
    } else {
      handleCartExpiration();
    }

    // Set up cart update listener
    setupCartUpdateListener();

    // Optional: Periodically check cart expiration (every 5 minutes)
    // This catches cases where the user leaves the tab open
    setInterval(handleCartExpiration, 5 * 60 * 1000);
  }

  // Expose a manual trigger function for testing
  window.shopifyCartExpiration = {
    check: handleCartExpiration,
    getCart: getCart,
    getTimestamp: getCartTimestamp,
    setTimestamp: setCartTimestamp,
    removeTimestamp: removeCartTimestamp,
    forceExpire: function() {
      // Set timestamp to 6 days ago for testing
      const sixDaysAgo = Date.now() - (6 * 24 * 60 * 60 * 1000);
      setCartTimestamp(sixDaysAgo);
      log('Timestamp set to 6 days ago for testing');
      handleCartExpiration();
    }
  };

  // Start the handler
  init();
})();